import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { TierRank, TierRankGradient } from '@root/entities/tier-rank'
import { NumericFormat } from '@root/shared/core/format'
import { useOptionalAtom } from '@root/shared/lib'
import { useTranslation } from '@root/shared/localization'

import { premiumFontCss } from '../constants'

import type { PremiumMetaModel } from '../../atoms'
import type { CurrencyModel } from '@root/entities/currency'

type Props = {
  currency: CurrencyModel
  premiumInfo: PremiumMetaModel | undefined
}

export const PremiumCurrencyInfoHeading: React.FC<Props> = ({
  currency,
  premiumInfo,
}) => {
  const premium = useOptionalAtom(premiumInfo?.premium)
  const supplyFormatted = NumericFormat.formatPreset(
    currency.supply,
    'currency-amount-short',
  )
  const tier = useOptionalAtom(premiumInfo?.tier)
  const tierRank = tier ? TierRank.fromPremiumTier(tier) : undefined
  const { t } = useTranslation()

  return (
    <Root>
      <Heading>
        <Title
          ellipsis
          data-premium={premium ?? false}
          data-tier={tierRank ?? 'none'}
        >
          {currency.title}
        </Title>
      </Heading>
      <Ticker>{currency.ticker}</Ticker>
      <Supply>
        {t('premium.check.currency.supply', {
          supply: supplyFormatted,
          ticker: currency.ticker,
        })}
      </Supply>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  text-transform: uppercase;
  gap: 4px;
`

const Heading = styled.header`
  display: flex;
  align-items: center;
`

const Title = styled(Typography)`
  color: var(--text-12);
  font-size: 16px;
  font-weight: 700;

  &[data-premium='true'] {
    &[data-tier='none'] {
      ${premiumFontCss}
    }
    &[data-tier='gold'] {
      ${TierRankGradient.tierRankTextGradientCss('gold')}
    }
    &[data-tier='diamond'] {
      ${TierRankGradient.tierRankTextGradientCss('diamond')}
    }
    &[data-tier='platinum'] {
      ${TierRankGradient.tierRankTextGradientCss('platinum')}
    }
    &[data-tier='elite'] {
      ${TierRankGradient.tierRankTextGradientCss('elite')}
    }
  }
`

const Ticker = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'p',
})`
  color: var(--text-9);
`

const Supply = styled.p`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
`
